import * as z from "zod";
import { flag, pagination, uuidV7, version } from "../index";
import { organizationRef, shortText } from "./fields";

// The buying groups (50-api/party.md §2.10; ADR-110, ADR-116): a group is made of contacts. HubSpot is their master
// while it is the master of the directory, and they are only read; once Nexxt is, Backstage writes them (ADR-289).

/** By name. Without `archived`, the ones in use. */
export const listBuyingGroupsInput = z.object({
  /** Part of the name or of the customer code, case-insensitive. */
  q: z.string().trim().min(1).max(100).optional(),
  /** Only the archived ones (HubSpot no longer has them, or Backstage archived them), instead of the others. */
  archived: flag.optional(),
  ...pagination({ defaultSize: 25, maxSize: 100 }),
});
export type ListBuyingGroupsInput = z.input<typeof listBuyingGroupsInput>;

export const getBuyingGroupInput = z.object({ buyingGroupId: z.uuid() });
export type GetBuyingGroupInput = z.input<typeof getBuyingGroupInput>;

const buyingGroupFields = z.object({
  name: shortText(200),
  /** Customer code of Tomorrow on the group (`cus_number`). */
  customerCode: shortText(20).nullable(),
  /** The contacts of the group, replaced whole: contacts of the directory in use. */
  contactIds: z.array(z.uuid()).max(500),
});

/** A new group, with the id from the client (ADR-046). */
export const createBuyingGroupInput = buyingGroupFields
  .partial({ customerCode: true, contactIds: true })
  .extend({ id: uuidV7 });
export type CreateBuyingGroupInput = z.input<typeof createBuyingGroupInput>;

/** PATCH: an absent field stays as it is. */
export const updateBuyingGroupInput = buyingGroupFields.partial().extend({ buyingGroupId: z.uuid(), version });
export type UpdateBuyingGroupInput = z.input<typeof updateBuyingGroupInput>;
export const updateBuyingGroupBody = updateBuyingGroupInput.omit({ buyingGroupId: true });

/** An action on a group, with the version the client read. */
export const buyingGroupActionInput = z.object({ buyingGroupId: z.uuid(), version });
export type BuyingGroupActionInput = z.input<typeof buyingGroupActionInput>;
export const buyingGroupVersionBody = buyingGroupActionInput.omit({ buyingGroupId: true });

export const buyingGroupSummaryDto = z
  .object({
    id: z.uuid(),
    name: z.string(),
    /** Customer code of Tomorrow on the group (`cus_number`). */
    customerCode: z.string().nullable().optional(),
    hubspotId: z.string().nullable(),
    contactCount: z.number().int(),
    archivedAt: z.iso.datetime().nullable(),
  })
  .meta({ id: "BuyingGroupSummary" });

/** A contact of a group, with the organization it is a person of. */
const buyingGroupContactDto = z.object({
  id: z.uuid(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  email: z.string().nullable(),
  organization: organizationRef,
  archivedAt: z.iso.datetime().nullable(),
});

export const buyingGroupDto = buyingGroupSummaryDto
  .extend({ contacts: z.array(buyingGroupContactDto), version: z.number().int() })
  .meta({ id: "BuyingGroup" });
